import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = axios.create({ baseURL: "/api" });

// Inject auth token on every request
API.interceptors.request.use((config) => {
  try {
    const stored = JSON.parse(localStorage.getItem("sentinel_auth"));
    if (stored?.access_token) {
      config.headers.Authorization = `Bearer ${stored.access_token}`;
    }
  } catch {}
  return config;
});

// On 401, clear auth and reload to login screen
let _reloading = false;
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !_reloading) {
      const url = err.config?.url || "";
      // Don't redirect on login/refresh/setup endpoints
      if (!url.includes("/auth/")) {
        // Only clear if we actually had a token (avoids clearing during initial load)
        const stored = localStorage.getItem("sentinel_auth");
        if (stored) {
          _reloading = true;
          localStorage.removeItem("sentinel_auth");
          window.location.reload();
        }
      }
    }
    return Promise.reject(err);
  }
);

export function useApi(endpoint, intervalMs = 0) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const fetch = useCallback(async () => {
    try {
      const res = await API.get(endpoint);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetch();
    if (intervalMs > 0) {
      const t = setInterval(fetch, intervalMs);
      return () => clearInterval(t);
    }
  }, [fetch, intervalMs]);

  return { data, loading, error, refetch: fetch };
}

export { API };
